import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleExclamation,
  faRefresh
} from "@fortawesome/pro-solid-svg-icons";

import { T, useT } from "@repo/transifex";
import { useDeviceData } from "~/providers/app";
import { Button, DialogLoading, TillCard } from "~/components";
import { authActions, useAuth } from "~/providers/store/auth";
import { LicenseLayout } from "~/routes/license/LicenseLayout.tsx";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { useUnassignedTillsBySchool } from "~/api/tills/hooks/use-tills";
import { useAssignTill } from "~/api/tills/hooks/use-assign-till";
import { type Till } from "~/api/tills/types";
import { Select } from "~/components/Inputs/select";

export const LicensePage = () => {
  const { tillId } = useAuth();
  const [selectedSchoolId, setSelectedSchoolId] = useState<string | null>(null);
  const [selectedTillId, setSelectedTillId] = useState<string | null>(null);

  const { deviceId } = useDeviceData();
  const t = useT();

  const { data: till, status: tillStatus } = useCurrentTill();

  const { data, status, refetch } = useUnassignedTillsBySchool();
  const { mutate: assignTill, status: assignStatus } = useAssignTill();

  const schoolsOptions = useMemo(() => {
    if (!data) return [];

    return data.map((school) => ({
      value: school.schoolId,
      label: `${school.name} (${school.tills.length})`
    }));
  }, [data]);

  const tillsOptions = useMemo(() => {
    if (!data) return [];

    const schoolData = data.find(
      (school) => school.schoolId === selectedSchoolId
    );

    if (!schoolData) return [];

    const getTillTypeText = (type: Till["type"]) => {
      switch (type) {
        case "attended":
          return t("Attended");
        case "self-service":
          return t("Self Service");
        case "ticket-validation":
          return t("Ticket Validation");
        case "kiosk":
          return t("Kiosk");
        case "simple-checkout":
          return t("Simple Checkout");
        case "collect":
          return t("Collect");
        default:
          return "";
      }
    };

    return schoolData.tills.map((till) => ({
      value: till.id,
      label: `${till.name} - ${getTillTypeText(till.type)}`
    }));
  }, [data, selectedSchoolId, t]);

  if (
    status === "pending" ||
    assignStatus === "pending" ||
    (tillId !== null && tillStatus === "pending")
  ) {
    return (
      <LicenseLayout>
        <DialogLoading />
      </LicenseLayout>
    );
  }

  if (status === "error") {
    return (
      <LicenseLayout>
        <div className="flex flex-col gap-4">
          <h1 className="text-center text-2xl font-bold text-danger-dark">
            <T _str="Could not load tills" />
          </h1>

          <p className="text-center">
            <T _str="There was an error fetching tills, please check your connection and try again" />
          </p>
        </div>

        <Button onClick={() => refetch()}>
          <div className="flex items-center gap-2">
            <FontAwesomeIcon fontSize={16} icon={faRefresh} />
            <T _str="Retry" />
          </div>
        </Button>
      </LicenseLayout>
    );
  }

  if (tillStatus === "success") {
    return (
      <LicenseLayout>
        <h1 className="text-center text-2xl font-bold">
          <T _str="Device already has a license" />
        </h1>

        {till.deviceId === deviceId ? (
          <Link to="/home">
            <Button className="w-full">
              <T _str="Back to POS" />
            </Button>
          </Link>
        ) : (
          <Button onClick={authActions.logoutTill}>
            <T _str="Assign new license" />
          </Button>
        )}
      </LicenseLayout>
    );
  }

  if (data.length > 0) {
    return (
      <LicenseLayout>
        <div className="flex flex-col gap-4">
          <h1 className="text-center text-2xl font-bold">
            <T _str="Let's get you started!" />
          </h1>
          <p className="text-center">
            <T _str="To begin, please choose a till below" />
          </p>
        </div>

        <div className="flex flex-col gap-5">
          <TillCard name="This device">
            <div className="flex w-full flex-col gap-1">
              <Select
                onChange={(value) => {
                  if (value) {
                    setSelectedSchoolId(value.value);
                  }
                }}
                options={schoolsOptions}
                placeholder={t("Choose a salesplace")}
                isSearchable={false}
              />

              <Select
                onChange={(value) => {
                  if (value) {
                    setSelectedTillId(value.value);
                  }
                }}
                options={tillsOptions}
                placeholder={t("Choose a license")}
                isSearchable={false}
              />
            </div>
          </TillCard>

          <Button
            disabled={!selectedTillId}
            onClick={() => {
              if (!selectedTillId || !deviceId) {
                throw new Error("Till or device id is missing");
              }

              assignTill({
                tillId: selectedTillId,
                deviceId
              });
            }}
          >
            <T _str="Assign license" />
          </Button>
        </div>
      </LicenseLayout>
    );
  }

  return (
    <LicenseLayout>
      <div className="flex flex-col gap-4">
        <h1 className="text-center text-2xl font-bold">
          <T _str="Oops, we can’t find any tills" />
        </h1>
        <p className="text-center">
          <T _str="Please create a till to continue" />
        </p>
      </div>

      <div className="flex flex-col gap-5">
        <TillCard name="This device">
          <div className="flex w-full items-center justify-center gap-2 rounded-lg bg-danger-backdrop p-4">
            <FontAwesomeIcon
              className="text-danger-dark"
              fontSize={18}
              icon={faCircleExclamation}
            />
            <h1 className="text-lg font-bold text-danger-dark">
              <T _str="No tills detected" />
            </h1>
          </div>
        </TillCard>

        <Button onClick={() => refetch()}>
          <div className="flex items-center gap-2">
            <FontAwesomeIcon fontSize={16} icon={faRefresh} />
            <T _str="Refresh" />
          </div>
        </Button>
      </div>
    </LicenseLayout>
  );
};
